import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Table, Modal, Form } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import { recipeAPI } from '../api/recipeAPI'
import { locationAPI } from '../api/locationAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDate, formatDateTime, formatUnitCost } from '../utils/commonUtils'
import { reasonOptions } from '../utils/disposeUtils'
import { getWasteBadgeVariant } from '../utils/wasteUtils'
import PickInventoryModal from '../components/PickInventoryModal'
import ConfirmationModal from '../components/ConfirmationModal'
import CopyableText from '../components/CopyableText'
import QuantityPicker from '../components/QuantityPicker'
import QuantityDisplay from '../components/QuantityDisplay'
import Select from 'react-select'
import '../styles/selectControl.css'

function ProductionDetailScreen() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [productionLot, setProductionLot] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    // Add inventory modal state
    const [showAddInventoryModal, setShowAddInventoryModal] = useState(false)
    const [selectedIngredientForAdd, setSelectedIngredientForAdd] = useState(null)
    const [addingInventory, setAddingInventory] = useState(false)

    // Return inventory modal state
    const [showReturnModal, setShowReturnModal] = useState(false)
    const [selectedAllocationForReturn, setSelectedAllocationForReturn] = useState(null)
    const [returnQuantity, setReturnQuantity] = useState('')
    const [returnLocationId, setReturnLocationId] = useState('')
    const [returningInventory, setReturningInventory] = useState(false)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState('')

    // Dispose inventory modal state
    const [showDisposeModal, setShowDisposeModal] = useState(false)
    const [selectedAllocationForDispose, setSelectedAllocationForDispose] = useState(null)
    const [disposeQuantity, setDisposeQuantity] = useState('')
    const [disposeReason, setDisposeReason] = useState('')
    const [disposeComments, setDisposeComments] = useState('')
    const [disposingInventory, setDisposingInventory] = useState(false)

    // Cancel modal state
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)


    useEffect(() => {
        fetchProductionLot()
        fetchWarehouses()
    }, [id])

    useEffect(() => {
        if (selectedWarehouse) {
            fetchLocations(selectedWarehouse)
        } else {
            setLocations([])
        }
    }, [selectedWarehouse])

    const fetchProductionLot = async () => {
        try {
            setLoading(true)
            const data = await productionAPI.getById(id)
            setProductionLot(data)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to load production LOT')
        } finally {
            setLoading(false)
        }
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchLocations = async (warehouseId) => {
        try {
            const data = await locationAPI.getAll(warehouseId)
            const activeLocations = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocations(activeLocations)
        } catch (error) {
            console.error('Error fetching locations:', error)
            setLocations([])
        }
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'in_progress':
                return <Badge bg="primary">In Progress</Badge>
            case 'completed':
                return <Badge bg="success">Completed</Badge>
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>
            default:
                return <Badge bg="light" text="dark">{status}</Badge>
        }
    }

    // Handle opening add inventory modal
    const openAddInventoryModal = (lotIngredient) => {
        setSelectedIngredientForAdd(lotIngredient)
        setShowAddInventoryModal(true)
    }

    // Handle add inventory confirmation
    const handleAddInventoryConfirm = async (allocations) => {
        if (!selectedIngredientForAdd) return

        setAddingInventory(true)
        setError(null)

        try {
            // Convert allocations to the format expected by the API
            const apiAllocations = Object.entries(allocations).map(([inventoryItemId, quantity]) => ({
                ingredient_id: selectedIngredientForAdd.recipe_ingredient,
                inventory_item_id: parseInt(inventoryItemId),
                quantity: parseFloat(quantity)
            })).filter(a => a.quantity > 0)

            if (apiAllocations.length === 0) {
                setError('No valid allocations to add')
                return
            }

            await productionAPI.addInventory(id, apiAllocations)
            setSuccessMessage('Inventory added successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to add inventory')
        } finally {
            setAddingInventory(false)
            setShowAddInventoryModal(false)
            setSelectedIngredientForAdd(null)
        }
    }

    // Handle opening return modal
    const openReturnModal = (allocation) => {
        const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
        setSelectedAllocationForReturn({ ...allocation, _available: available })
        setReturnQuantity(available.toString())
        setReturnLocationId('')
        setSelectedWarehouse('')
        setShowReturnModal(true)
    }

    // Handle return inventory
    const handleReturnInventory = async () => {
        if (!selectedAllocationForReturn || !returnLocationId || !returnQuantity) {
            setError('Please fill in all return fields')
            return
        }

        const qty = parseFloat(returnQuantity)
        if (isNaN(qty) || qty <= 0 || qty > selectedAllocationForReturn._available) {
            setError('Invalid return quantity')
            return
        }

        setReturningInventory(true)
        setError(null)

        try {
            await productionAPI.returnInventory(id, [{
                production_lot_inventory_item_id: selectedAllocationForReturn._id,
                quantity: qty,
                return_location_id: parseInt(returnLocationId)
            }])
            setSuccessMessage('Inventory returned successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to return inventory')
        } finally {
            setReturningInventory(false)
            setShowReturnModal(false)
            setSelectedAllocationForReturn(null)
        }
    }

    // Handle cancel production
    const handleCancelProduction = async () => {
        setCancelling(true)
        setError(null)

        try {
            await productionAPI.cancel(id)
            setSuccessMessage('Production LOT cancelled')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to cancel production LOT')
        } finally {
            setCancelling(false)
            setShowCancelModal(false)
        }
    }

    // Handle opening dispose modal
    const openDisposeModal = (allocation) => {
        const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
        setSelectedAllocationForDispose({ ...allocation, _available: available })
        setDisposeQuantity('')
        setDisposeReason('')
        setDisposeComments('')
        setShowDisposeModal(true)
    }

    // Handle dispose inventory
    const handleDisposeInventory = async () => {
        if (!selectedAllocationForDispose || !disposeQuantity || !disposeReason) {
            setError('Please fill in quantity and reason')
            return
        }

        const qty = parseFloat(disposeQuantity)
        if (isNaN(qty) || qty <= 0 || qty > selectedAllocationForDispose._available) {
            setError('Invalid dispose quantity')
            return
        }

        setDisposingInventory(true)
        setError(null)

        try {
            await productionAPI.disposeInventory(id, [{
                production_lot_inventory_item_id: selectedAllocationForDispose._id,
                quantity: qty,
                reason: disposeReason,
                comments: disposeComments
            }])
            setSuccessMessage('Inventory disposed successfully')
            await fetchProductionLot()
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to dispose inventory')
        } finally {
            setDisposingInventory(false)
            setShowDisposeModal(false)
            setSelectedAllocationForDispose(null)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!productionLot) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error || 'Production lot not found'}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const isInProgress = productionLot.status === 'in_progress'

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Production LOT: {productionLot.lot_number}</h1>
                </Col>
                <Col xs="auto">
                    {getStatusBadge(productionLot.status)}
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            {/* Production LOT Details */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Details</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col xs="auto">
                            {productionLot.output_stock_item_image ? (
                                <img
                                    src={productionLot.output_stock_item_image}
                                    alt={productionLot.output_stock_item_name}
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '140px',
                                        height: '140px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                        </Col>
                        <Col md={5}>
                            <p><strong>Recipe:</strong>{' '}
                                <Link to={`/recipe/${productionLot.recipe}`} state={{ backTo: `/production/${productionLot._id}`, backLabel: 'Back to Production LOT' }}>
                                    {productionLot.recipe_name}
                                </Link>
                            </p>
                            {productionLot.recipe_item_number && (
                                <p><strong>Item #:</strong> {productionLot.recipe_item_number}</p>
                            )}
                            <p><strong>Output Product:</strong> {productionLot.output_stock_item_name}</p>
                            {productionLot.output_stock_item_brand_name && (
                                <p><strong>Brand:</strong> {productionLot.output_stock_item_brand_name}</p>
                            )}
                            <p><strong>GTIN:</strong> <CopyableText value={productionLot.output_stock_item_gtin} toastLabel="GTIN" /></p>
                            <p><strong>Target Quantity:</strong> {productionLot.target_quantity}</p>
                            <p><strong>Production Location:</strong> {productionLot.production_location_name}</p>
                            {productionLot.output_bb_date && (
                                <p><strong>Best Before Date:</strong> {formatDate(productionLot.output_bb_date)}</p>
                            )}
                        </Col>
                        <Col md={6}>
                            <p><strong>Started At:</strong> {formatDateTime(productionLot.started_at)}</p>
                            <p><strong>Started By:</strong> {productionLot.started_by_name}</p>
                            {productionLot.completed_at && (
                                <>
                                    <p><strong>Completed At:</strong> {formatDateTime(productionLot.completed_at)}</p>
                                    <p><strong>Completed By:</strong> {productionLot.completed_by_name}</p>
                                </>
                            )}
                            {productionLot.actual_quantity_produced && (
                                <p><strong>Actual Quantity Produced:</strong> {productionLot.actual_quantity_produced}</p>
                            )}
                            {parseFloat(productionLot.quantity_disassembled || 0) > 0 && (
                                <>
                                    <p><strong>Quantity Disassembled:</strong> {productionLot.quantity_disassembled}</p>
                                    {productionLot.disassembled_at && (
                                        <p><strong>Last Disassembled At:</strong> {formatDateTime(productionLot.disassembled_at)}</p>
                                    )}
                                    {productionLot.disassembled_by_name && (
                                        <p><strong>Disassembled By:</strong> {productionLot.disassembled_by_name}</p>
                                    )}
                                </>
                            )}
                            {productionLot.total_cost && (
                                <p><strong>Total Cost:</strong> ${parseFloat(productionLot.total_cost).toFixed(2)}</p>
                            )}
                            {productionLot.unit_cost && (
                                <p><strong>Unit Cost:</strong> {formatUnitCost(productionLot.unit_cost)}</p>
                            )}
                        </Col>
                    </Row>
                    {productionLot.notes && (
                        <Row>
                            <Col>
                                <p><strong>Notes:</strong> {productionLot.notes}</p>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Ingredients */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Ingredients</h5>
                </Card.Header>
                <Card.Body>
                    {productionLot.lot_ingredients && productionLot.lot_ingredients.length > 0 ? (
                        productionLot.lot_ingredients.map((lotIngredient) => {
                            const ri = lotIngredient.recipe_ingredient_data
                            const isWeight = ri?.input_mode === 'weight'
                            const casePack = ri?.case_pack || 1
                            const uom = ri?.unit_of_measure || ''
                            return (
                            <Card key={lotIngredient._id} className="mb-3">
                                <Card.Header>
                                    <div className="d-flex align-items-center">
                                        <span style={{ width: '160px', flexShrink: 0 }}>
                                            {ri?.item_family
                                                ? ''
                                                : <CopyableText value={ri?.gtin} toastLabel="GTIN" />}
                                        </span>
                                        <strong>{lotIngredient.ingredient_name}</strong>
                                        {isInProgress && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="ms-auto"
                                                onClick={() => openAddInventoryModal(lotIngredient)}
                                            >
                                                Add More
                                            </Button>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                        {(() => {
                                            const wastePctVal = parseFloat(ri?.waste_percentage) || 0
                                            const requiredQty = parseFloat(lotIngredient.required_quantity)
                                            const recipeQty = wastePctVal > 0 ? requiredQty / (1 + wastePctVal / 100) : requiredQty
                                            return (
                                                <>
                                                    {wastePctVal > 0 && (
                                                        <Badge bg="secondary">
                                                            Recipe Qty: {isWeight ? (
                                                                <span>{parseFloat(recipeQty.toFixed(3))} {ri?.weight_unit || 'lbs'}
                                                                    {ri?.weight_per_case
                                                                        ? <span> (<QuantityDisplay casePack={casePack} uom={uom} value={recipeQty / ri.weight_per_case} />)</span>
                                                                        : null}
                                                                </span>
                                                            ) : (
                                                                <QuantityDisplay casePack={casePack} uom={uom} value={recipeQty} />
                                                            )}
                                                        </Badge>
                                                    )}
                                                    <Badge bg="info">
                                                        {wastePctVal > 0 ? 'Required (incl. waste): ' : 'Required: '}{isWeight ? (
                                                            <span>{requiredQty} {ri?.weight_unit || 'lbs'}
                                                                {ri?.weight_per_case
                                                                    ? <span> (<QuantityDisplay casePack={casePack} uom={uom} value={requiredQty / ri.weight_per_case} />)</span>
                                                                    : null}
                                                            </span>
                                                        ) : (
                                                            <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.required_quantity} />
                                                        )}
                                                    </Badge>
                                                </>
                                            )
                                        })()}
                                        <Badge bg={
                                            isWeight
                                                ? 'secondary'
                                                : parseFloat(lotIngredient.picked_quantity) >= parseFloat(lotIngredient.required_quantity)
                                                    ? 'success' : 'warning'
                                        }>
                                            Picked: {isWeight && ri?.item_family
                                                ? <span>{parseFloat(lotIngredient.picked_quantity)} units</span>
                                                : <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.picked_quantity} />}
                                        </Badge>
                                        {lotIngredient.used_quantity !== null && (
                                            <Badge bg="secondary">
                                                Used: {isWeight && ri?.item_family
                                                    ? <span>{parseFloat(lotIngredient.used_quantity)} units</span>
                                                    : <QuantityDisplay casePack={casePack} uom={uom} value={lotIngredient.used_quantity} />}
                                            </Badge>
                                        )}
                                        {productionLot.status === 'completed' && (() => {
                                            const wastePct = lotIngredient.actual_waste_percentage != null
                                                ? parseFloat(lotIngredient.actual_waste_percentage)
                                                : null
                                            return (
                                                <Badge bg={wastePct != null ? getWasteBadgeVariant(wastePct) : 'secondary'} className="rounded-pill">
                                                    Waste: {wastePct != null ? `${wastePct.toFixed(2)}%` : '--'}
                                                </Badge>
                                            )
                                        })()}
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {lotIngredient.inventory_allocations && lotIngredient.inventory_allocations.length > 0 ? (
                                        <Table size="sm" striped style={{ tableLayout: 'fixed' }}>
                                            {(() => {
                                                const hasUsed = lotIngredient.used_quantity !== null
                                                const hasDisposed = isInProgress || lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_disposed || 0) > 0)
                                                const hasReturned = isInProgress || lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_returned || 0) > 0)
                                                const hasDisassembled = lotIngredient.inventory_allocations.some(a => parseFloat(a.quantity_disassembled || 0) > 0)
                                                return (
                                                    <>
                                                    <colgroup>
                                                        <col style={{ width: '18%' }} />
                                                        <col style={{ width: '14%' }} />
                                                        <col style={{ width: '13%' }} />
                                                        <col style={{ width: '14%' }} />
                                                        <col style={{ width: '8%' }} />
                                                        {hasUsed && <col style={{ width: '7%' }} />}
                                                        {(hasUsed || hasReturned) && <col style={{ width: '9%' }} />}
                                                        {hasDisposed && <col style={{ width: '9%' }} />}
                                                        {hasDisassembled && <col style={{ width: '9%' }} />}
                                                        {isInProgress && <col style={{ width: '17%' }} />}
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th>GTIN</th>
                                                            <th>Inventory Item</th>
                                                            <th>LOT</th>
                                                            <th>Original Location</th>
                                                            <th>Allocated</th>
                                                            {hasUsed && <th>Used</th>}
                                                            {(hasUsed || hasReturned) && <th>Returned</th>}
                                                            {hasDisposed && <th>Disposed</th>}
                                                            {hasDisassembled && <th>Restored</th>}
                                                            {isInProgress && <th className="text-end">Actions</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {lotIngredient.inventory_allocations.map((allocation) => {
                                                            const available = parseFloat(allocation.quantity_allocated) - parseFloat(allocation.quantity_returned || 0) - parseFloat(allocation.quantity_disposed || 0)
                                                            const aCp = allocation.stock_item_case_pack || 1
                                                            const aUom = allocation.stock_item_uom || ''
                                                            return (
                                                                <tr key={allocation._id}>
                                                                    <td><CopyableText value={allocation.stock_item_gtin} toastLabel="GTIN" /></td>
                                                                    <td>{allocation.stock_item_name || '-'}</td>
                                                                    <td>{allocation.original_lot || '-'}</td>
                                                                    <td>{allocation.original_location_name || '-'}</td>
                                                                    <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_allocated} /></td>
                                                                    {hasUsed && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_used || 0} /></td>}
                                                                    {(hasUsed || hasReturned) && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_returned || 0} /></td>}
                                                                    {hasDisposed && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_disposed || 0} /></td>}
                                                                    {hasDisassembled && <td><QuantityDisplay casePack={aCp} uom={aUom} value={allocation.quantity_disassembled || 0} /></td>}
                                                                    {isInProgress && (
                                                                        <td>
                                                                            <div className="d-flex gap-1 justify-content-end">
                                                                                <Button
                                                                                    variant="outline-warning"
                                                                                    size="sm"
                                                                                    onClick={() => openReturnModal(allocation)}
                                                                                    disabled={available <= 0}
                                                                                >
                                                                                    Return
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => openDisposeModal(allocation)}
                                                                                    disabled={available <= 0}
                                                                                >
                                                                                    Dispose
                                                                                </Button>
                                                                            </div>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                    </>
                                                )
                                            })()}
                                        </Table>
                                    ) : (
                                        <p className="text-muted mb-0">No inventory allocated</p>
                                    )}
                                </Card.Body>
                            </Card>
                        )})
                    ) : (
                        <p className="text-muted">No ingredients in this production LOT</p>
                    )}
                </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
                {isInProgress && (
                    <>
                        <Button
                            variant="success"
                            onClick={() => navigate(`/production/${id}/finalize`)}
                        >
                            Finalize Production
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => setShowCancelModal(true)}
                        >
                            Cancel Production
                        </Button>
                    </>
                )}
                <Button
                    variant="outline-primary"
                    onClick={() => navigate('/productions')}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>

            {/* Add Inventory Modal */}
            {selectedIngredientForAdd && (
                <PickInventoryModal
                    show={showAddInventoryModal}
                    onHide={() => {
                        setShowAddInventoryModal(false)
                        setSelectedIngredientForAdd(null)
                    }}
                    ingredient={{
                        _id: selectedIngredientForAdd.recipe_ingredient,
                        ingredient_name: selectedIngredientForAdd.ingredient_name,
                        ...selectedIngredientForAdd.recipe_ingredient_data
                    }}
                    requiredQuantity={0} // No specific requirement for additional picks
                    recipeId={productionLot.recipe}
                    onConfirm={handleAddInventoryConfirm}
                    existingAllocations={null}
                />
            )}

            {/* Return Inventory Modal */}
            <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Return Inventory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAllocationForReturn && (
                        <>
                            <p>
                                <strong>Item:</strong> {selectedAllocationForReturn.stock_item_name}
                            </p>
                            <p>
                                <strong>Available to Return:</strong> {selectedAllocationForReturn._available}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity to Return</Form.Label>
                                <QuantityPicker
                                    casePack={selectedAllocationForReturn.stock_item_case_pack || 1}
                                    uom={selectedAllocationForReturn.stock_item_uom || ''}
                                    value={returnQuantity}
                                    onChange={(qty) => setReturnQuantity(qty)}
                                    max={selectedAllocationForReturn._available}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Warehouse</Form.Label>
                                <Select
                                    value={warehouses
                                        .map(wh => ({ value: wh._id, label: wh.name }))
                                        .find(opt => opt.value === selectedWarehouse) || null}
                                    onChange={(opt) => setSelectedWarehouse(opt?.value || '')}
                                    options={warehouses.map(wh => ({ value: wh._id, label: wh.name }))}
                                    placeholder="Select Warehouse..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Return to Location</Form.Label>
                                <Select
                                    value={locations
                                        .map(loc => ({ value: loc._id, label: loc.name }))
                                        .find(opt => opt.value === returnLocationId) || null}
                                    onChange={(opt) => setReturnLocationId(opt?.value || '')}
                                    options={locations.map(loc => ({ value: loc._id, label: loc.name }))}
                                    isDisabled={!selectedWarehouse}
                                    placeholder="Select Location..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReturnModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleReturnInventory}
                        disabled={returningInventory || !returnLocationId || !returnQuantity}
                    >
                        {returningInventory ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Returning...
                            </>
                        ) : (
                            'Return Inventory'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Dispose Inventory Modal */}
            <Modal show={showDisposeModal} onHide={() => setShowDisposeModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Dispose Inventory</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAllocationForDispose && (
                        <>
                            <p>
                                <strong>Item:</strong> {selectedAllocationForDispose.stock_item_name}
                            </p>
                            <p>
                                <strong>Available to Dispose:</strong> {selectedAllocationForDispose._available}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity to Dispose <span className="text-danger">*</span></Form.Label>
                                <QuantityPicker
                                    casePack={selectedAllocationForDispose.stock_item_case_pack || 1}
                                    uom={selectedAllocationForDispose.stock_item_uom || ''}
                                    value={disposeQuantity}
                                    onChange={(qty) => setDisposeQuantity(qty)}
                                    max={selectedAllocationForDispose._available}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={reasonOptions
                                        .filter(opt => opt.value)
                                        .map(opt => ({ value: opt.value, label: opt.label }))
                                        .find(opt => opt.value === disposeReason) || null}
                                    onChange={(opt) => setDisposeReason(opt?.value || '')}
                                    options={reasonOptions
                                        .filter(opt => opt.value)
                                        .map(opt => ({ value: opt.value, label: opt.label }))}
                                    placeholder="Select Reason..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Comments</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={disposeComments}
                                    onChange={(e) => setDisposeComments(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDisposeModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDisposeInventory}
                        disabled={disposingInventory || !disposeQuantity || !disposeReason}
                    >
                        {disposingInventory ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Disposing...
                            </>
                        ) : (
                            'Dispose Inventory'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Confirmation Modal */}
            <ConfirmationModal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                onConfirm={handleCancelProduction}
                title="Cancel Production"
                message={`Are you sure you want to cancel this production LOT? Inventory will remain at the production location (${productionLot.production_location_name}).`}
                confirmButtonText={cancelling ? 'Cancelling...' : 'Cancel Production'}
                confirmButtonVariant="danger"
            />

        </Container>
    )
}

export default ProductionDetailScreen
